/**
 * WHICH MONTHS THE AUDIENCE PICKER OFFERS, AND WHICH ONE IT OPENS ON - with no clock in this app.
 *
 * `getAudienceBreakdown` answers one SEALED month, and a month is sealed once, after it ends, by a
 * daily job over "the month before the clock's" (dashboard-cabinet.controller.ts, `MONTH_NOT_SEALED`).
 * No read lists the sealed months, and this app has no clock and must not have one
 * (`features/cabinet/format.ts`, C11 and D9). So the months are counted from the SERVER'S day:
 * `getDashboard` with no range answers `to` = "the most recent completed day" on the server clock
 * (authors-api.yaml, `getDashboard` `to`). The day after it is the server's today, its month is
 * the open one, and the month before that is the newest month that CAN be sealed - the default.
 *
 * ★ "CAN BE", NOT "IS". On the first day of a month the seal may not have run yet, and a month that
 * ended before the platform began sealing never will be. Either answers 404 `MONTH_NOT_SEALED`, and
 * the screen says "not sealed yet" for it - it never guesses another month on its own. The open
 * month is offered too, and always answers that way, which is the truthful answer about it.
 *
 * ★ TWO YEARS BACK (design 19): the months before launch are counted once, at launch, from the old
 * apps' likes, saves, comments and tips (`audience-fill`, from 2025-02), and the picker must reach
 * them - the first build's year cut off the first eight. Offering more is this one constant.
 */
import { monthOfInstant, shiftMonth } from '../../features/calendar/domain/month';
import type { MonthKey } from '../../features/calendar/domain/month';

export const MONTHS_OFFERED = 24;

export interface AudienceMonths {
  /** The server's current month: never sealed, offered so the author can see that it is not. */
  readonly open: MonthKey;
  /** The ended months, newest first. The first is the default. */
  readonly ended: readonly [MonthKey, ...MonthKey[]];
}

const DAY_MS = 86_400_000;

/**
 * The months, from the server's most recent completed day (`YYYY-MM-DD`), or `undefined` when that
 * is not a day. Arithmetic on a value the server sent - a parse, which C11 allows - never a clock.
 */
export function audienceMonths(newestCompletedDay: string): AudienceMonths | undefined {
  const completed = Date.parse(`${newestCompletedDay}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(newestCompletedDay) || Number.isNaN(completed)) {
    return undefined;
  }
  const open = monthOfInstant(new Date(completed + DAY_MS).toISOString());
  if (open === undefined) {
    return undefined;
  }
  const newestEnded = shiftMonth(open, -1);
  const older = Array.from({ length: MONTHS_OFFERED - 1 }, (_unused, index) =>
    shiftMonth(open, -(index + 2)),
  );
  return { open, ended: [newestEnded, ...older] };
}

/** `2026-07` → `2026-07-01`: "the UTC calendar month, as its first day", as the read asks for it. */
export function firstDayOf(month: MonthKey): string {
  return `${month}-01`;
}
